import { ApiResponse } from "~/types/api";
import { axiosInstances } from "./Axios";
import { clearCsrfToken, setCsrfToken } from "./Csrf";

export interface IInstitute {
  id: number;
  name: string;
  primaryColor: string;
  secondaryColor: string;
  primaryLightColor: string;
  logoUrl: string;
  vocationalCurrencyUsesUnits: boolean;
}

export interface IEmploymentTypeResponse {
  id: number;
  name: string;
  profiling_tool_required: boolean;
}

export interface IInstituteResponse {
  id: number;
  name: string;
  primary_color: string;
  secondary_color: string;
  primary_light_color: string;
  logo_url: string;
  vocational_currency_uses_units: boolean;
  points_file_attachment_enabled: boolean;
  file_attachment_required: boolean;
  educator_points_entry_enabled: boolean;
  vcm_study_example_description: string | null;
  vcm_study_example_justification: string | null;
  vcm_study_example_info_url: string | null;
  vcm_experience_example_description: string | null;
  vcm_experience_example_justification: string | null;
  vcm_experience_example_info_url: string | null;
  vcm_course_request_email: string | null;
  professional_qualification_help_link_label: string | null;
  professional_qualification_help_link_url: string | null;
}

export interface IChangePasswordFormData {
  currentPassword: string | null;
  password: string | null;
  confirmPassword: string | null;
}

export interface IUserCheckSSORequest {
  user: {
    email: string;
  };
}

export interface IUserCheckSSOResponse {
  login_type: string;
  login_url: string | null;
}

export interface IUserSignInSSORequest {
  user_pool: string;
  code: string;
}

export interface IUserSignInRequest {
  user: {
    email: string;
    password: string;
  };
}

export interface IUserResponse {
  id: number;
  email: string;
  given_name: string;
  family_name: string;
  is_educator: boolean;
  change_password_required: boolean;
  terms_accepted: boolean;
  registrations_require_approval_enabled: boolean;
  institute: IInstituteResponse;
  employment_type: IEmploymentTypeResponse;
  vcm_enabled: boolean;
}

export interface IUserSignInResponse {
  user: IUserResponse;
  csrf_token: string;
}

export interface ISsoSignInResponse {
  authentication_token: string;
  user: IUserResponse;
}

export interface IUserSignOutResponse {
  message?: string;
  logout_type?: string;
  logout_url?: string;
}

export const checkSSO = (checkSSORequest: IUserCheckSSORequest): Promise<IUserCheckSSOResponse> =>
  axiosInstances
    .public()
    .post("/sso/sign_in.json", checkSSORequest)
    .then((response) => response.data);

export const signInSSO = (signInSSORequest: IUserSignInSSORequest): Promise<ISsoSignInResponse> =>
  axiosInstances
    .public()
    .get(`/sso/signed_in/${signInSSORequest.user_pool}.json?code=${signInSSORequest.code}`)
    .then((response) => response.data);

export const signIn = (signInRequest: IUserSignInRequest): Promise<IUserSignInResponse> =>
  axiosInstances
    .public()
    .post<IUserSignInResponse>("/api/v2/login.json", signInRequest)
    .then((response) => {
      setCsrfToken(response.data.csrf_token);
      return response.data;
    });

export const signOut = (): Promise<IUserSignOutResponse> =>
  axiosInstances
    .secured()
    .delete("/api/v2/logout.json")
    .then((response) => {
      clearCsrfToken();
      return response.data;
    });

export const requestForgotPassword = (email: string) =>
  axiosInstances
    .public()
    .post("/users/password.json", { user: { email } })
    .then((response) => response.data);

export const resetPassword = ({ token, password }: { token: string; password: string }) =>
  axiosInstances
    .secured()
    .put("/users/password.json", { user: { reset_password_token: token, password } })
    .then((response) => response.data);

export const updatePassword = ({ currentPassword, password }: IChangePasswordFormData) =>
  axiosInstances
    .secured()
    .put("/api/v1/users/password", {
      user: {
        current_password: currentPassword,
        password,
        password_confirmation: password,
      },
    })
    .then((response) => response.data);

export const getUser = (): Promise<ApiResponse<IUserResponse>> =>
  axiosInstances
    .secured()
    .get("/api/v1/users/me")
    .then((response) => response.data);

export const acceptTerm = (userId: number) =>
  axiosInstances
    .secured()
    .get(`/api/v1/users/${userId}/accept_terms.json`)
    .then((response) => response.data);
